import { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { Button } from '../base/Button';

interface EmbedCodeModalProps {
  embedCode: string;
  onClose: () => void;
}

export const EmbedCodeModal = ({ embedCode, onClose }: EmbedCodeModalProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      // 最も確実な方法: テキストエリアを使用
      const textArea = document.createElement('textarea');
      textArea.value = embedCode;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
      
      // フォールバック: Clipboard API
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // 最終手段: テキストエリアを表示して手動コピーを促す
      const textarea = document.getElementById('embed-code-textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.select();
        alert('自動コピーに失敗しました。Ctrl+C(Mac: Cmd+C)を押してコピーしてください。');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-xl font-semibold text-gray-900">埋め込みコード</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600">
            以下のコードをコピーして、あなたのウェブサイトに貼り付けてください。
          </p>
          
          <div className="relative">
            <pre className="w-full bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-words max-h-[400px] overflow-y-auto">
              <code>{embedCode}</code>
            </pre>
            
            <Button
              variant="primary"
              size="sm"
              onClick={handleCopy}
              icon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              className="absolute top-3 right-3"
            >
              {copied ? 'コピーしました！' : 'コピー'}
            </Button>
          </div>
          
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-teal-900 mb-2 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-lightbulb-line text-base"></i>
              </div>
              使い方
            </h4>
            <ul className="text-sm text-teal-800 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-teal-600 mt-0.5">•</span>
                <span>「コピー」ボタンをクリックしてコードをコピー</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 mt-0.5">•</span>
                <span>HTMLファイルの任意の場所に貼り付け</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 mt-0.5">•</span>
                <span>WordPress、Wix、Squarespaceなどでも使用可能</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-600 mt-0.5">•</span>
                <span>レスポンシブ対応で、モバイルでも正常に動作</span>
              </li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-amber-900 mb-2 flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-code-s-slash-line text-base"></i>
              </div>
              コードの特徴
            </h4>
            <ul className="text-sm text-amber-800 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-0.5">•</span>
                <span><strong>超軽量：</strong>外部ライブラリ不要のVanilla JS/CSS</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-0.5">•</span>
                <span><strong>高速読み込み：</strong>ページ速度に影響なし</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-0.5">•</span>
                <span><strong>レスポンシブ：</strong>全デバイスで最適表示</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-0.5">•</span>
                <span><strong>Google Drive対応：</strong>直リンクに自動変換済み</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
          <Button variant="secondary" onClick={onClose}>
            閉じる
          </Button>
        </div>
      </div>
    </div>
  );
};
